'use client';

import { useState, useEffect, useRef } from 'react';
import { Lightbulb, Sparkles } from 'lucide-react';
import MontessoriToggle from '../montessori/MontessoriToggle';
import { getInspiredPrompt, getCurrentHolidayName } from '@/lib/inspire-me-prompts';

interface StepIdeaProps {
  value: string;
  onChange: (value: string) => void;
  onQuickGenerate?: () => void; // Optional quick generation handler
  montessoriMode?: boolean;
  onMontessoriModeChange?: (enabled: boolean) => void;
  isSubscriber?: boolean;
}

// INSPIRATION_PROMPTS now dynamically generated in @/lib/inspire-me-prompts
// - 50+ unique prompts across 10 categories
// - Seasonal awareness and holiday detection
// - Anti-repetition tracking for truly unique stories every time

export default function StepIdea({ value, onChange, onQuickGenerate, montessoriMode = false, onMontessoriModeChange, isSubscriber = false }: StepIdeaProps) {
  const [inspiring, setInspiring] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Check if we have any text (even just one word)
  const hasAnyText = value.trim().length > 0;
  const wordCount = value.trim().split(/\s+/).filter(w => w.length > 0).length;

  useEffect(() => {
    // Auto-focus on mount
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  const handleInspireMe = () => {
    setInspiring(true);
    setTimeout(() => {
      // Use the new smart inspiration system with seasonal awareness
      const inspiredPrompt = getInspiredPrompt();
      onChange(inspiredPrompt);
      setInspiring(false);
    }, 300);
  };

  // Expand short input into a full prompt
  const expandToPrompt = (input: string): string => {
    const trimmed = input.trim();
    
    // If already a decent length, use as-is
    if (trimmed.split(/\s+/).length >= 8) {
      return trimmed;
    }

    // Expand short input into a story prompt
    const templates = [
      `A magical story about ${trimmed}`,
      `An adventure featuring ${trimmed}`,
      `A tale of ${trimmed} discovering something wonderful`,
      `${trimmed} goes on an exciting journey`,
      `The amazing adventures of ${trimmed}`,
    ];

    return templates[Math.floor(Math.random() * templates.length)];
  };

  const handleButtonClick = () => {
    if (hasAnyText && onQuickGenerate) {
      // Expand the input if it's short (fewer than 8 words)
      if (wordCount < 8) {
        const expandedPrompt = expandToPrompt(value);
        onChange(expandedPrompt);
      }
      // Start generation
      onQuickGenerate();
    } else {
      // No text - inspire them!
      handleInspireMe();
    }
  };

  return (
    <div style={{ maxWidth: '100%', width: '100%', margin: '0 auto' }}>
      {/* Header Row with Info and Inspire Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '0.75rem',
      }}>
        <div style={{ flex: 1 }}>
          <p style={{ 
            fontSize: '0.875rem', 
            color: 'var(--color-text-secondary)', 
            margin: 0,
            fontWeight: 500
          }}>
            3 credits per audio picture book
          </p>
        </div>
        <button
          type="button"
          onClick={handleInspireMe}
          disabled={inspiring}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            cursor: inspiring ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
          }}
          onMouseEnter={(e) => {
            if (!inspiring) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.3)';
          }}
          aria-label="Get random story idea inspiration"
        >
          {inspiring ? (
            <>
              <Lightbulb size={16} className="spinning" />
              <span>Thinking...</span>
            </>
          ) : (
            <>
              <Sparkles size={16} />
              <span>Inspire Me {getCurrentHolidayName()}</span>
            </>
          )}
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          placeholder="Example: A curious bunny goes on an adventure to find the rainbow's end and discovers a magical garden..."
          className="idea-textarea"
          style={{
            width: '100%',
            minHeight: '120px',
            maxHeight: '300px',
            padding: '1rem',
            fontSize: '1rem',
            lineHeight: 1.6,
            borderRadius: 'var(--r-xl, 20px)',
            border: '2px solid #e0d5f0',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit',
            transition: 'all 0.2s ease',
            scrollMarginTop: '120px', // For mobile keyboard
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = '#C5B4FF';
            e.currentTarget.style.boxShadow = '0 0 0 4px rgba(197, 180, 255, 0.15)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#e0d5f0';
            e.currentTarget.style.boxShadow = 'none';
          }}
          aria-label="Story idea"
        />
      </div>

      {/* Next Step Button - Only shows when there's text and can proceed */}
      {hasAnyText && onQuickGenerate && (
        <button
          type="button"
          onClick={() => {
            // Expand the input if it's short (fewer than 8 words)
            if (wordCount < 8) {
              const expandedPrompt = expandToPrompt(value);
              onChange(expandedPrompt);
            }
            // Start generation
            onQuickGenerate();
          }}
          disabled={inspiring}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '1.25rem 2.5rem',
            background: 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '60px',
            fontSize: '1.125rem',
            fontWeight: 700,
            cursor: inspiring ? 'not-allowed' : 'pointer',
            boxShadow: '0 8px 24px rgba(168, 216, 230, 0.4)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            margin: '0 auto',
          }}
          onMouseEnter={(e) => {
            if (!inspiring) {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(168, 216, 230, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 216, 230, 0.4)';
          }}
          aria-label="Proceed to next step"
        >
          <Sparkles size={22} />
          <span>Next Step</span>
        </button>
      )}

      {/* Montessori Mode */}
      {onMontessoriModeChange && (
        <div style={{ marginTop: '2rem' }}>
          <MontessoriToggle
            enabled={montessoriMode}
            isSubscriber={isSubscriber}
            onChange={onMontessoriModeChange}
            onUpsell={() => {
              // Redirect to login page where users can choose login method
              window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname);
            }}
          />
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

